import { z } from 'zod';
import { IProfession, professionSchema } from './IProfession';

// Schemas (source of truth)
export const minimizedProfessionSchema = professionSchema.pick({
	dateCreated: true,
	_id: true,
	projectId: true,
	tradeGroup: true,
	tradeId: true,
	organizationId: true,
});

/**
 * What an embedded profession looks like ON THE WAY OUT, after
 * `responseEnrichment.plugin.ts` has merged the full ProjectConfig profession
 * over the minimized one the document stores.
 *
 * This schema exists because a response schema that stops at
 * `minimizedProfessionSchema` does not merely under-describe the payload — it
 * DELETES from it. `customSerializerCompiler` parses the response, and Zod
 * strips keys the schema does not declare, so enrichment would add
 * `backgroundColor` / `activeProjects` in `preSerialization` and serialization
 * would take them straight back out. Downstream that renders as an invisible
 * profession chip: `getProfessionBackgroundColor` finds neither field, returns
 * `''`, and `ProfessionDisplayWithTradeIcon` paints a white icon and white text
 * on a chip with no background.
 *
 * Every field is optional: enrichment is best-effort (it no-ops for non-frontend
 * requests, on `/stream` routes, and when the request carries no projectId), and
 * an un-enriched minimized profession must still satisfy the schema.
 */
export const professionForDisplaySchema = minimizedProfessionSchema.extend(
	professionSchema
		.pick({
			backgroundColor: true,
			sortIndex: true,
			activeProjects: true,
			contractor: true,
			specialty: true,
		})
		.partial().shape
);

// Types (derived from schemas)
export type IMinimizedProfession = z.infer<typeof minimizedProfessionSchema>;

// Display type: base fields required, extra IProfession fields optional.
// Accepts both IProfession (full) and IMinimizedProfession (minimal) values.
export type IProfessionForDisplay = IMinimizedProfession & Partial<Omit<IProfession, keyof IMinimizedProfession>>;
